"use client";
import Button, { BtnType } from "@/components/common/buttons/Button";
import InputSelect from "@/components/common/inputs/InputSelect";
import InputTextfield from "@/components/common/inputs/InputTextfield";
import Google from "@/components/SocialButton/Google";
import { APP_ROUTES } from "@/constants/routes";
import { setErrorMess } from "@/lib/features/statusSlice";
import { cn } from "@/lib/utils";
import { OptionItem } from "@/models/general";
import countryService from "@/services/main/countryServices";
import UserServices from "@/services/main/UserServices";
import { Box, Divider, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { useDispatch } from "react-redux";
import { RegisterForm } from "../page";
import classes from "../styles.module.scss";
import PopupAcceptTermService from "./PopupAcceptTermService/index.";

interface Step1Props {
  handleFormSubmit?: () => void;
  register: UseFormRegister<RegisterForm>;
  errors: FieldErrors;
  setValue: UseFormSetValue<RegisterForm>;
  watch: (name: string) => string;
  trigger: UseFormTrigger<any>;
  control: Control<RegisterForm>;
  clearErrors: UseFormClearErrors<RegisterForm>;
  setError: UseFormSetError<RegisterForm>;
  // isEmailExisted?: boolean;
  setCountries?: (countries: OptionItem[]) => void;
  countries?: OptionItem[];
  type?: string | null;
}

export default function Step1({
  handleFormSubmit,
  register,
  errors,
  setValue,
  watch,
  trigger,
  control,
  clearErrors,
  setError,
  // isEmailExisted,
  setCountries,
  countries = [],
  type,
}: Step1Props) {
  const t = useTranslations("messages");
  const locale = useLocale();
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect");
  const dispatch = useDispatch();
  const [openPopupTerms, setOpenPopupTerms] = useState(false);
  const [isEmailExisted, setIsEmailExisted] = useState<boolean>(false);

  const fetchCountries = useMutation({
    mutationFn: async () => {
      return await countryService.getAllCountries({ take: 9999 });
    },
    onSuccess: (data: {
      data: { id: number; name: string; shortName: string }[];
    }) => {
      const formattedCountries =
        data?.data?.map(
          ({
            id,
            name,
            shortName,
          }: {
            id: number;
            name: string;
            shortName: string;
          }) => ({
            id,
            name,
            shortName,
          })
        ) || [];
      setCountries?.(formattedCountries);
    },
    onError: (error) => {
      dispatch(setErrorMess(error));
    },
  });

  /** Handle submit thủ công */
  const handleSubmit = async () => {
    // Validate form trước
    const isValid = await trigger([
      "firstName",
      "lastName",
      "email",
      "country",
    ]);

    if (!isValid) {
      // const firstError = Object.keys(errors)[0];
      // const errorMessage = errors[firstError]?.message || t("common_error_message");
      // dispatch(setErrorMess(errorMessage));
      return;
    }

    // Check email tồn tại
    const emailValue = watch("email");
    try {
      await UserServices.checkEmail(emailValue);
      setIsEmailExisted(false);
      // Email không tồn tại → Mở popup terms
      setOpenPopupTerms(true);
    } catch (e: any) {
      if (e.data?.existed) {
        setIsEmailExisted(true);
        return;
      }
      // Lỗi khác
      dispatch(setErrorMess(e.message || t("common_error_message")));
    }
  };

  /** Handle Accept Terms Popup: */
  const handleAcceptTerms = () => {
    setOpenPopupTerms(false);
    handleFormSubmit?.(); // Gọi trực tiếp
  };

  useEffect(() => {
    fetchCountries.mutate(); // Gọi mutation đúng cách
  }, []);

  /** Lấy nội dung điều khoản */

  const handleRenderSubTitle = useMemo(() => {
    switch (type) {
      case "plan":
        return t("register_page_plan_sub_title");
      case "report":
        return t("register_page_report_sub_title");
      case "trial":
        return t("register_page_trial_sub_title");
      default:
        return t("register_page_sub_title");
    }
  }, [type, t]);
  return (
    <>
      <Box>
        <h2
          className="mt-2 mb-6 text-sm/6 font-medium tracking-[0.25px] text-gray-80 self-stretch"
          translation-key="register_page_sub_title"
          translation-plan-key="register_page_plan_sub_title"
          translation-report-key="register_page_report_sub_title"
        >
          {handleRenderSubTitle}
        </h2>

        {/* <InvitationCodeInput
        register={register}
        errors={errors}
        setValue={setValue}
        watch={watch}
        clearErrors={clearErrors}
        setError={setError}
      /> */}

        <Box className=" flex flex-col gap-[16px]">
          <Box className="flex flex-col md:grid md:grid-cols-2 gap-x-[24px] gap-y-4">
            <InputTextfield
              tabIndex={0}
              autoFocus
              translation-key="field_first_name"
              translation-key-placeholder="field_first_name_placeholder"
              title={t("field_first_name")}
              placeholder={t("field_first_name_placeholder")}
              {...register("firstName")}
              errorMessage={
                errors?.firstName?.message
                  ? String(errors.firstName?.message)
                  : null
              }
              className="w-full"
            />
            <InputTextfield
              tabIndex={1}
              translation-key="field_last_name"
              translation-key-placeholder="field_last_name_placeholder"
              title={t("field_last_name")}
              placeholder={t("field_last_name_placeholder")}
              {...register("lastName")}
              errorMessage={
                errors?.lastName?.message
                  ? String(errors.lastName?.message)
                  : null
              }
              className="w-full"
            />
          </Box>

          <Box>
            <InputTextfield
              tabIndex={2}
              transiton-key="field_email_business"
              transiton-key-placeholder="field_email_business_placeholder"
              title={t("field_email_business")}
              placeholder={t("field_email_business_placeholder")}
              {...register("email")}
              errorMessage={
                errors?.email?.message ? String(errors.email?.message) : null
              }
              className="w-full"
            />
          </Box>

          <Box>
            <InputSelect
              transiton-key="field_country"
              transiton-key-placeholder="field_country_placeholder"
              control={control}
              name="country"
              label={t("field_country")}
              options={countries.map(({ id, name, shortName }) => ({
                value: id,
                label: name,
                shortName: shortName,
              }))}
              errors={errors}
              trigger={trigger}
              placeholder={t("field_country_placeholder")}
            />
          </Box>
        </Box>
        {isEmailExisted && (
          <Box className="mt-4 ">
            <Typography className="!text-red-500 !font-medium !text-sm">
              {t.rich("register_page_email_existed_message", {
                loginLink: (chunks) => (
                  <Link
                    href={APP_ROUTES(locale).LOGIN.to}
                    className="text-blue-600 underline register-page-email-existed-login-link"
                  >
                    {chunks}
                  </Link>
                ),
                forgotLink: (chunks) => (
                  <Link
                    href={APP_ROUTES(locale).FORGOTPASSWORD.to}
                    className="text-blue-600 underline register-page-email-existed-forgot-link"
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </Typography>
          </Box>
        )}

        <Button
          btnType={BtnType.Raised}
          className={cn(classes.btnAuthForm, "register-continue-button")}
          sx={{ textTransform: "none", mt: "30px" }}
          translation-key="auth_button_continue"
          onClick={handleSubmit}
        >
          <p className="m-0">{t("auth_button_continue")}</p>
        </Button>

        {/** POPUP TERMS SERVICE */}

        {/* <Button
        btnType={BtnType.Raised}
        className={classes.btnAuthForm}
        sx={{ textTransform: "none", mt: "30px" }}
        type="submit"
        translation-key="auth_button_continue"
      >
        <p className="m-0">{t("auth_button_continue")}</p>
      </Button> */}

        <p
          className="font-normal text-xs leading-4 text-center mt-2 mb-0 text-gray-80"
          translation-key="register_page_agree_message_1"
        >
          {t("register_page_agree_message_1")}{" "}
          <Link
            translation-key="footer_term_and_privacy"
            className="font-medium text-xs text-gray-90 register-page-term-service-link"
            href={APP_ROUTES(locale).TERMS_AND_SERVICE.to}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("footer_term_and_privacy")}
          </Link>
          {t("common_and")}
          <Link
            className="font-medium text-xs text-gray-90 register-page-privacy-policy-link"
            href={`https://www.cimigo.com/${locale}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("footer_privacy")}.
          </Link>
        </p>

        <p
          className="font-medium  text-sm leading-6 text-center mt-4 mb-0 text-gray-80"
          translation-key="register_page_already_have_account"
        >
          {t("register_page_already_have_account")}{" "}
          <Link
            href={`${APP_ROUTES(locale).LOGIN.to}${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}
            className="underline text-cimigoBlue register-page-login-now"
            translation-key="register_page_login_now"
          >
            {t("register_page_login_now")}
          </Link>
        </p>

        <Divider sx={{ margin: "24px 0px" }}>
          <p
            className="text-base font-normal m-0 text-gray-80"
            translation-key="register_page_register_with"
          >
            {t("register_page_register_with")}
          </p>
        </Divider>

        <Box className="flex flex-col gap-[16px]">
          <Google translation-key="register_page_btn_with">
            {t("register_page_btn_with", { name: "Google" }) as string}
          </Google>
          {/* <Microsoft translation-key="register_page_btn_with">
          {t("register_page_btn_with", { name: "Microsoft" }) as string}
        </Microsoft> */}
        </Box>
      </Box>
      <PopupAcceptTermService
        open={openPopupTerms}
        setOpen={setOpenPopupTerms}
        onSubmit={handleAcceptTerms}
      />
    </>
  );
}
